import { isManualPaywallActive } from '@hector/shared-types';
import { useCabinetMemberMe } from '@/hooks/useCabinetMemberMe';

/**
 * Determines whether the manual paywall overlay should block the current user.
 *
 * Canonical signal: `cabinet_members.feature_flags.force_paywall === true`.
 * This is the flag exposed in the Super Admin drawer ("Paywall forcé"), edited
 * via PATCH /api/admin/users/:userId/feature-flags. The legacy `activate_paywall`
 * boolean column is being phased out — it was never wired to this UI control
 * (cf. CLAUDE.md FEATURE_FLAG_CATALOG, TICKET-113).
 *
 * Robustness contract — designed to NEVER block a user by accident:
 *  - The flag check itself is `isManualPaywallActive` (`@hector/shared-types`),
 *    shared with the Word add-in: strict boolean literal `true` on a plain
 *    object, anything else → paywall stays OFF.
 *  - Loading, error, missing member → paywall stays OFF (fail-open).
 *  - The query is scoped to the current cabinet membership, so toggling the flag
 *    for cabinet A does not block the user when they swap to cabinet B.
 *
 * If a future bug somehow returns `force_paywall: "true"` (string) or stores it
 * in the wrong column, we'd rather fail-open and not block paying customers than
 * fail-closed and lock everyone out.
 */
export function useManualPaywall(): { isPaywallActive: boolean; isLoading: boolean } {
  const { data: member, isLoading, isError } = useCabinetMemberMe();

  if (isLoading) return { isPaywallActive: false, isLoading: true };

  if (isError || !member) return { isPaywallActive: false, isLoading: false };

  return { isPaywallActive: isManualPaywallActive(member.feature_flags), isLoading: false };
}
